<template>
  <SearchWrapper
    v-model:filtersData="selectedData"
    v-model:currentSort="selectedSorting"
    :isLoading="isLoading"
    :count="count"
    :filters="searchFilters"
    :hasTotalPrice="packagesStore.displayTotalPriceToggle()"
    :showSort="venueList.showSort"
  >
    <template v-slot:title>
      <span v-t="{ path: '{x} venues available for:', plural: count, args: { x: count } }" />&nbsp;
      <button
        v-if="!isLoading && currentPackage"
        type="button"
        class="db-tw-text-link db-tw-font-normal db-tw-cursor-pointer db-venue-link"
        v-on:click.prevent="showModalPackage"
      >
        {{ currentPackage.galaxyPackage.name }}
      </button>
    </template>
    <template v-slot:results="{ seeTotalPrice }">
      <DbVenueResults
        :seeTotalPrice="seeTotalPrice"
        :venues="sortedResults"
        :descriptionDisplay="venueList.shortDescriptionDisplay"
        :showRoomAmenities="venueList.showRoomAmenities"
        :showRoomAttributes="venueList.showRoomAttributes"
        :nbrGridLayout="hotel.db2_event_rooms_layout"
        v-on:click-details="showDetailModal"
        v-on:select-venue="selectVenue"
      />
    </template>

    <template
      v-if="availableVenues.length === 0"
      v-slot:no-results-title
    >
      {{
        $t(
          'Unfortunately, there is no availability for the selected date and/or number of attendees',
        )
      }}
    </template>

    <template
      v-if="availableVenues.length === 0"
      v-slot:no-results-message
    >
      <div>{{ $t('If possible, please search for alternative option or contact us') }}</div>

      <a
        v-if="funnel.requestAQuoteLink?.url"
        :href="funnel.requestAQuoteLink?.url"
        :target="funnel.requestAQuoteLink?.target ? '_blank' : undefined"
        class="db-button-tertiary db-button-large db-tw-mt-6 md:db-tw-mt-9"
      >
        {{ $t('Contact Hotel') }}
      </a>
    </template>
  </SearchWrapper>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'

import type { GalaxyMeetingRoom } from '@/types/galaxy-data/meeting-room'
import { useGalaxyStaticSearch } from '@/composables/features/useGalaxyStaticSearch'
import type { SearchSort } from '@/features/SearchWrapper/SearchWrapperTypes'

import { useConfigurations } from '@/stores/configurations'
import { usePackagesStore } from '@/stores/packages'
import { useCartStore } from '@/stores/cart'
import { storeToRefs } from 'pinia'

import { useFunnelRouter } from '@/AppBookingFunnel/composables/funnel-router'
import { useModalsStore } from '@/stores/modals'
import type { FilterItem } from '@/types/galaxy-data/filters'
import { useI18n } from 'vue-i18n'

const funnelRouter = useFunnelRouter()

const configStore = useConfigurations()
const { venueList, funnel } = storeToRefs(configStore)

const packagesStore = usePackagesStore()
const { apiLoading, availableVenues, meetingRoomID } = storeToRefs(packagesStore)

const cartStore = useCartStore()
const { isLoading: cartLoading, currentPackage } = storeToRefs(cartStore)

const modalStore = useModalsStore()

const hotel = window.db2BookingFunnel.getHotel()

const props = withDefaults(
  defineProps<{
    /** Additional filters not visible in the view, but to use when filtering */
    extraFilters?: Record<string, unknown>
  }>(),
  {
    extraFilters: () => ({}),
  },
)

const selectedData = ref({})
const allFilters = computed(() => {
  return {
    ...props.extraFilters,
    ...selectedData.value,
  }
})
/** Sorting `value` currently selected */
const selectedSorting = ref<SearchSort>('recommended')

const isLoading = computed(() => {
  if (!(apiLoading.value || searchLoading.value || cartLoading.value)) {
    configStore.resetIdle()
  }
  return apiLoading.value || searchLoading.value || cartLoading.value
})

/** Init the search store */
const {
  isLoading: searchLoading,
  sortedResults,
  count,
} = useGalaxyStaticSearch({
  rawData: availableVenues,
  filters: allFilters,
  currentSort: selectedSorting,
})

function showModalPackage() {
  if (!currentPackage.value) {
    throw new Error('No current package')
  }

  modalStore.computeFunctionRoomSetupsFromCurrentPackage()
  modalStore.showPackageModal(currentPackage.value?.galaxyPackage)
}

async function showDetailModal(meetingRoom: GalaxyMeetingRoom) {
  modalStore.computeFunctionRoomSetupsFromCurrentPackage() // Compute to define functionRoomSetups (use in room setup section of the modal)
  modalStore.showMeetingRoomModal(
    meetingRoom,
    currentPackage.value?.galaxyPackage?.functionRoomSetups,
  )
}

let watchermeetingRoomID = true

// Check if the venue is not already provide through the query url "meetingRoomID"
watch(
  [meetingRoomID],
  () => {
    // Case where the query is not define
    // (either undefined or string type: meaning waiting for api fetching)
    if (!watchermeetingRoomID || !meetingRoomID.value || typeof meetingRoomID.value === 'string') {
      return
    }
    selectVenue(meetingRoomID.value)
  },
  { immediate: true },
)

function selectVenue(venue: GalaxyMeetingRoom) {
  if (venue) {
    watchermeetingRoomID = false
    cartStore.setFunctionRoom(venue.db2_eventroomid)
    meetingRoomID.value = venue
    funnelRouter.goToPackageConfiguration()
  }
}

const searchFilters: FilterItem[] = []
const { t } = useI18n()

let amenitiesOptions: { value: string; label: string }[] = []

if (configStore.venueList.showFilters) {
  watch(sortedResults, () => {
    if (sortedResults.value.length) {
      searchFilters.splice(0)
      // filter byAmenities to exclude unused setup room
      if (configStore.venueList.showFilterByAmenities) {
        amenitiesOptions = []
        availableVenues.value.forEach((item) => {
          item.meeting_room_amenities.forEach((amenitie) => {
            if (!amenitiesOptions.find((el) => el.value === amenitie.id.toString()))
              amenitiesOptions.push({
                value: amenitie.id.toString(),
                label: amenitie.name,
              })
          })
        })

        searchFilters.push({
          label: 'Amenities',
          key: 'amenities_value',
          options: amenitiesOptions,
        })
      }

      if (configStore.venueList.showFilterByRoomSize) {
        searchFilters.push({
          label: t('Event Room Size'),
          key: 'total_area',
          type: 'range',
          options: [
            {
              value: '<20',
              label: t('{x}m2', { x: '< 20' }), // should not use locale (not editable text)
            },
            {
              value: '<=50',
              label: t('{x}m2', { x: '20 - 50' }), // should not use locale (not editable text)
            },
            {
              value: '<=100',
              label: t('{x}m2', { x: '51 - 100' }), // should not use locale (not editable text)
            },
            {
              value: '<=150',
              label: t('{x}m2', { x: '101 - 150' }), // should not use locale (not editable text)
            },
            {
              value: '<=200',
              label: t('{x}m2', { x: '151 - 200' }), // should not use locale (not editable text)
            },
            {
              value: '>200',
              label: t('{x}m2', { x: '+201' }), // should not use locale (not editable text)
            },
          ],
        })
      }
    }
  })
}
</script>

<style scoped>
.db-venue-link {
  font-style: var(--db_link_font_style);
}
</style>
